import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useLivePoll } from '../../hooks/useLivePoll';
import { useToasts } from '../../components/Toasts';

/**
 * Nadzor prijava - obaveštava čim klijent prijavi problem ili napiše poruku.
 *
 * Stoji u ljusci, ne na stranici prijava: čovek iz podrške najčešće gleda nešto
 * drugo, a baš tada prijava i stigne. Do sada je saznavao tek kad sam otvori
 * spisak.
 *
 * Pita server **šta je novo od poslednjeg puta** (`/activity?since=`), pa je
 * poziv jeftin i ne zavisi od toga koliko tiketa ukupno ima. Prvi poziv ne
 * vraća ništa - služi da uzme vreme servera, da se panel ne zatrpa istorijom
 * pri otvaranju.
 *
 * Zašto anketiranje a ne SSE: `EventSource` ne ume `Authorization` header, pa bi
 * sysadmin token morao u URL - a taj token otvara celu platformu i u URL-u
 * završava po logovima.
 */

type NewTicket = {
  id: string;
  subject: string;
  severity: 'low' | 'normal' | 'high' | 'critical';
  tenantName: string;
  createdAt: string;
};

type NewMessage = {
  id: string;
  ticketId: string;
  subject: string;
  tenantName: string;
  authorName: string | null;
  body: string;
  createdAt: string;
};

type Activity = { now: string; newTickets: NewTicket[]; newMessages: NewMessage[] };

const POLL_MS = 5_000;
/** Kraći odlomak poruke - obaveštenje nije mesto za ceo tekst. */
const PREVIEW = 90;

const short = (s: string) => (s.length > PREVIEW ? `${s.slice(0, PREVIEW)}…` : s);

/**
 * Obaveštenje pregledača - vidi se i kad je kartica u pozadini.
 *
 * Šalje se **samo kad panel nije u prvom planu**: kad čovek gleda ekran, toast
 * je dovoljan, a dupla obaveštenja za istu stvar smetaju.
 */
function notifyOutside(title: string, body: string, onOpen: () => void) {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (!document.hidden) return;
  try {
    const n = new Notification(title, { body, tag: 'efikasa-support' });
    // Klik vraca coveka u panel i otvara BAS tu prijavu - inace bi se vratio na
    // spisak pa je opet trazio.
    n.onclick = () => { window.focus(); onOpen(); n.close(); };
  } catch { /* pregledac koji odbije obavestenje ne sme da obori nadzor */ }
}

export function useSupportWatch() {
  const navigate = useNavigate();
  const { push } = useToasts();
  const since = useRef<string | null>(null);
  const [unread, setUnread] = useState(0);

  /**
   * Otvori prijavu - i sa spiska i van njega.
   *
   * Ide kroz upit u adresi (`?ticket=`), a ne kroz stanje komponente: obavestenje
   * moze da stigne sa bilo koje stranice, pa mora da postoji putanja koja sama
   * po sebi znaci „otvori ovaj tiket". Isto vazi i kad je covek vec na spisku.
   */
  const openTicket = useCallback((ticketId: string) => {
    navigate(`/support?ticket=${ticketId}`);
  }, [navigate]);

  const check = useCallback(async () => {
    try {
      const q = since.current ? `?since=${encodeURIComponent(since.current)}` : '';
      const res = await api.get<Activity>(`/admin/support/activity${q}`);

      // Prvi prolaz samo pamti vreme servera - bez obaveštenja o istoriji.
      const first = since.current === null;
      since.current = res.now;
      if (first) return;

      for (const t of res.newTickets) {
        const critical = t.severity === 'critical';
        push({
          title: `Nova prijava - ${t.tenantName}`,
          body: short(t.subject),
          tone: critical ? 'critical' : t.severity === 'high' ? 'warning' : 'info',
          onClick: () => openTicket(t.id),
        });
        notifyOutside(`Nova prijava - ${t.tenantName}`, t.subject, () => openTicket(t.id));
      }

      for (const m of res.newMessages) {
        push({
          title: `Poruka u prijavi - ${m.tenantName}`,
          body: `${m.authorName ? `${m.authorName}: ` : ''}${short(m.body)}`,
          onClick: () => openTicket(m.ticketId),
        });
        notifyOutside(`Poruka - ${m.tenantName}`, short(m.body), () => openTicket(m.ticketId));
      }

      const total = res.newTickets.length + res.newMessages.length;
      if (total > 0) setUnread((n) => n + total);
    } catch {
      // Prolazan prekid mreze ne sme da baci gresku preko panela; sledeci
      // prolaz nadoknadjuje, jer `since` nije pomeren.
    }
  }, [openTicket, push]);

  useEffect(() => { void check(); }, [check]);
  useLivePoll(check, POLL_MS);

  // Otvaranje spiska prijava je citanje - znacka se gasi.
  const clearUnread = useCallback(() => setUnread(0), []);

  return { unread, clearUnread };
}

/**
 * Traži dozvolu za obaveštenja pregledača.
 *
 * Mora iz korisničke radnje (klik) - pregledači odbijaju zahtev koji dolazi sam
 * od sebe pri učitavanju stranice.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}
